import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Config,
  predicateHelpers,
  useCreate,
  useDelete,
  useHttp,
  usePatch,
  useUpdate
} from "@/hooks/useHttp"

import type { GetUseMutationOptions, Params, Updater } from "@/hooks/useHttp"
import type {
  ReportDefinitionResponse,
  ReportJobResponse
} from "./useReports.types"

export const useGetReportsDefinitions = <
  T extends ReportDefinitionResponse,
  S = T
>(
  params?: Params<{ needle?: string }>,
  config?: Config<T, S>
) => {
  const { needle, ...rest } = params || {}
  const { DEFINITIONS } = RESOURCES_NAMES.REPORTS
  const url = needle ? `${DEFINITIONS}/${needle}` : DEFINITIONS
  const context = useHttp<T, S>(url, rest, config)

  return context
}

export const useGetReportsJobs = <T extends ReportJobResponse, S = T>(
  params?: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(RESOURCES_NAMES.REPORTS.JOBS, params, config)

  return context
}

export const useCreateReports = <T = ReportDefinitionResponse, S = T>(
  params?: Params,
  updater?: Updater<T, S>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useCreate(
    RESOURCES_NAMES.REPORTS.DEFINITIONS,
    params,
    updater,
    options
  )

  return context
}

export const usePatchReports = <T = ReportDefinitionResponse, S = T>(
  params?: Params,
  updater?: Updater<T, S>,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = usePatch(
    RESOURCES_NAMES.REPORTS.DEFINITIONS,
    params,
    updater,
    options
  )

  return context
}

export const useUpdateReports = () => {
  const context = useUpdate(
    RESOURCES_NAMES.REPORTS.DEFINITIONS,
    undefined,
    undefined,
    (queryClient) => ({
      onMutate: predicateHelpers.onMutate(queryClient, {
        include: [RESOURCES_NAMES.REPORTS.DEFINITIONS]
      }),
      onError: predicateHelpers.onError(queryClient)
    })
  )

  return context
}

export const useReportsGenerate = <T, S = T>(
  params: Params<{ needle: string; auth_token: string }>,
  config: Config<T, S>
) => {
  const { needle, ...rest } = params || {}
  const url = `${RESOURCES_NAMES.REPORTS.GENERATE}/${needle}`
  const context = useHttp<T, S>(url, rest, { retry: false, ...config })

  return context
}

export const useDeleteReports = () => {
  const context = useDelete(RESOURCES_NAMES.REPORTS.DEFINITIONS)

  return context
}
